import React from "react";
import { Link } from "react-router-dom";
import { Network, BookOpen, Clock } from "lucide-react";
import ScoreMeter from "./ScoreMeter";
import BookmarkButton from "./BookmarkButton";
import { TagBadgeRow } from "./TagFilters";

function evidenceCount(topic) {
    const ev = topic?.sections?.evidence_sections || {};
    return (ev.documented?.length || 0) + (ev.reported?.length || 0) + (ev.debated?.length || 0);
}

function formatUpdated(iso) {
    if (!iso) return null;
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
    } catch {
        return null;
    }
}

export default function TopicCard({ topic }) {
    if (!topic) return null;
    const sources = topic.sources?.length || 0;
    const connections = topic.related_topics?.length || 0;
    const updated = formatUpdated(topic.last_updated);

    return (
        <div className="relative group">
            <Link
                to={`/topic/${topic.slug}`}
                data-testid={`topic-card-${topic.slug}`}
                className="block bg-[#111827] border border-[#243044] rounded-xl p-6 hover:border-[#2F80ED] transition-all duration-300 hover:-translate-y-0.5"
            >
                {topic.category && (
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-medium mb-3 pr-10 inline-flex items-center gap-2">
                        {topic.category}
                        {topic.topic_layer === "foundational" ? (
                            <span
                                data-testid={`topic-card-layer-foundational-${topic.slug}`}
                                className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-[#56CCF2]/10 border border-[#56CCF2]/30 text-[#56CCF2] font-medium"
                                title="Foundational system — broader explainer"
                            >
                                Foundational
                            </span>
                        ) : null}
                        {topic.status === "under_investigation" ? (
                            <span
                                data-testid={`topic-card-status-under-${topic.slug}`}
                                className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded-sm bg-[#E0B341]/10 border border-[#E0B341]/30 text-[#E0B341] font-medium inline-flex items-center gap-1"
                                title="Under investigation — actively being researched"
                            >
                                <span className="w-1 h-1 rounded-full bg-[#E0B341] animate-pulse" />
                                Under investigation
                            </span>
                        ) : null}
                    </div>
                )}
                <h3 className="text-xl font-bold text-white leading-snug mb-2 group-hover:text-[#56CCF2] transition-colors pr-6">
                    {topic.title}
                </h3>
                {topic.subtitle && (
                    <p className="text-sm text-[#94A3B8] mb-4 line-clamp-2 leading-relaxed">{topic.subtitle}</p>
                )}
                {topic.summary && !topic.subtitle && (
                    <p className="text-sm text-[#94A3B8] mb-4 line-clamp-2 leading-relaxed">{topic.summary}</p>
                )}

                <div className="grid grid-cols-2 gap-4 mt-6 pt-6 border-t border-[#243044]">
                    <ScoreMeter label="Confidence" value={topic.confidence_score} kind="confidence" size="sm" />
                    <ScoreMeter label="Concern" value={topic.concern_level} kind="concern" size="sm" />
                </div>

                {topic.tags && topic.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-5">
                        {topic.tags.slice(0, 3).map((t) => (
                            <span key={t} className="text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-[#0B1220] border border-[#243044] text-[#94A3B8]">
                                {t}
                            </span>
                        ))}
                    </div>
                )}

                {((topic.geography && topic.geography.length) || topic.intensity) ? (
                    <div className="mt-3" data-testid="topic-card-tag-row">
                        <TagBadgeRow geography={topic.geography} intensity={topic.intensity} compact={false} />
                    </div>
                ) : null}

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-5 pt-4 border-t border-[#243044]/70 text-[10px] uppercase tracking-[0.15em] text-[#64748B]">
                    <span data-testid="topic-card-sources" className="inline-flex items-center gap-1.5">
                        <BookOpen className="w-3 h-3" />
                        {sources} {sources === 1 ? "source" : "sources"}
                    </span>
                    <span data-testid="topic-card-connections" className="inline-flex items-center gap-1.5">
                        <Network className="w-3 h-3" />
                        {connections} {connections === 1 ? "link" : "links"}
                    </span>
                    {updated && (
                        <span className="inline-flex items-center gap-1.5 ml-auto">
                            <Clock className="w-3 h-3" />
                            {updated}
                        </span>
                    )}
                </div>
            </Link>

            <div className="absolute top-3 right-3 z-10 opacity-70 hover:opacity-100 transition-opacity">
                <BookmarkButton
                    type="topic"
                    id={topic.slug}
                    title={topic.title}
                    subtitle={topic.category}
                    href={`/topic/${topic.slug}`}
                />
            </div>
        </div>
    );
}

export { evidenceCount };
